/**
 * Shared visual primitives for the 11 wizard steps.
 * Keeps each step file <150 lines while enforcing the light-shell theme.
 */
import { ArrowClockwise, CheckCircle, Warning, Info } from "@phosphor-icons/react";


export function WizardCard({ title, subtitle, children, right, testId }) {
  return (
    <section
      className="rounded-lg border border-slate-200 bg-white shadow-sm"
      data-testid={testId}>
      <header className="flex items-center gap-2 px-4 py-3 border-b border-slate-200">
        <div className="flex-1">
          <h3 className="text-sm font-bold text-slate-800">{title}</h3>
          {subtitle && <div className="text-[11px] text-slate-500">{subtitle}</div>}
        </div>
        {right}
      </header>
      <div className="p-4 space-y-3">{children}</div>
    </section>
  );
}


export function KV({ k, v, mono = true, testId }) {
  return (
    <div className="flex items-baseline gap-2 py-1 border-b border-slate-100 last:border-none"
          data-testid={testId}>
      <div className="text-[11px] text-slate-500 flex-1">{k}</div>
      <div className={`text-[13px] font-semibold text-slate-800 ${mono ? "font-mono" : ""}`}>
        {v ?? "—"}
      </div>
    </div>
  );
}


export function Pill({ tone = "slate", children, testId }) {
  const styles = {
    slate:   "bg-slate-100 text-slate-700 border-slate-200",
    green:   "bg-emerald-100 text-emerald-800 border-emerald-300",
    amber:   "bg-amber-100 text-amber-800 border-amber-300",
    red:     "bg-red-100 text-red-800 border-red-300",
    blue:    "bg-brand-blue/10 text-brand-blue border-brand-blue/30",
  }[tone];
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border
                          px-2 py-0.5 text-[10px] font-semibold ${styles}`}
          data-testid={testId}>
      {children}
    </span>
  );
}


export function LoadingBanner({ label }) {
  return (
    <div className="flex items-center gap-2 rounded border border-brand-blue/30
                        bg-brand-blue/5 px-3 py-2 text-[12px] text-brand-blue">
      <ArrowClockwise size={14} className="animate-spin" />
      {label || "Running…"}
    </div>
  );
}


export function StatusRow({ tone = "info", children }) {
  const map = {
    info:  { Icon: Info,         cls: "text-slate-700 border-slate-200 bg-slate-50" },
    ok:    { Icon: CheckCircle,  cls: "text-emerald-800 border-emerald-200 bg-emerald-50" },
    warn:  { Icon: Warning,      cls: "text-amber-800 border-amber-200 bg-amber-50" },
    error: { Icon: Warning,      cls: "text-red-800 border-red-200 bg-red-50" },
  }[tone] || { Icon: Info, cls: "" };
  const { Icon, cls } = map;
  return (
    <div className={`flex items-start gap-2 rounded border px-3 py-2 text-[12px] ${cls}`}>
      <Icon size={13} weight="duotone" className="mt-[1px] shrink-0" />
      <div>{children}</div>
    </div>
  );
}
